/** Marker dimensions stay in map units; zoom scales the entire board. */
export function territoryMarkerMetrics(radius: number, landscape = false, desktop = false, label = '') {
  const r = radius * (landscape ? (desktop ? 1.125 : 1.25) : 1)
  return { radius: r, fontSize: r * Math.min(1.12, 2.5 / Math.max(1, label.length)), strokeWidth: r * 0.09 }
}

interface MarkerTerritory {
  id: string
  adjacencies: readonly string[]
  position: { x: number; y: number }
  badgeAnchor?: { x: number; y: number }
}

/** One black dashed connection per adjacency, clipped to the ownership discs. */
export function territoryMarkerConnections(territories: readonly MarkerTerritory[], radius: number) {
  const byId = new Map(territories.map(t => [t.id, t]))
  const seen = new Set<string>()
  return territories.flatMap(a => a.adjacencies.flatMap(id => {
    const b = byId.get(id)
    const key = [a.id, id].sort().join('|')
    if (!b || seen.has(key)) return []
    seen.add(key)
    const from = a.badgeAnchor ?? a.position, to = b.badgeAnchor ?? b.position
    const dx = to.x - from.x, dy = to.y - from.y
    const length = Math.hypot(dx, dy)
    if (length <= radius * 2) return []
    const inset = radius / length
    return [{ key, aId: a.id, bId: id,
      from: { x: from.x + dx * inset, y: from.y + dy * inset },
      to: { x: to.x - dx * inset, y: to.y - dy * inset } }]
  }))
}


/** Marker faces must stay opaque even when the normal neutral territory tint is translucent. */
export function opaqueMarkerFill(fill: string, dimmed = false): string {
  const rgba = fill.match(/^rgba\(\s*([^,]+),\s*([^,]+),\s*([^,]+),[^)]+\)$/i)
  let opaque = rgba ? `rgb(${rgba[1]}, ${rgba[2]}, ${rgba[3]})` : fill
  if (/^#[0-9a-f]{8}$/i.test(opaque)) opaque = opaque.slice(0, 7)
  if (/^#[0-9a-f]{4}$/i.test(opaque)) opaque = opaque.slice(0, 4)
  if (!dimmed) return opaque
  const hex = opaque.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1]
  const expanded = hex?.length === 3 ? hex.split('').map(c => c + c).join('') : hex
  const rgb = opaque.match(/^rgb\(\s*([^,]+),\s*([^,]+),\s*([^)]+)\)$/i)
  const channels = expanded ? [0, 2, 4].map(i => parseInt(expanded.slice(i, i + 2), 16))
    : rgb ? rgb.slice(1).map(Number) : undefined
  // Darken an opaque colour instead of lowering opacity, which would reveal lines.
  return channels ? `rgb(${channels.map(value => Math.round(value * 0.88)).join(', ')})` : opaque
}

/** Black-only marker emphasis: selected targets get a heavier ring in either theme. */
export function territoryMarkerSelectionRing(radius: number, selected: boolean) {
  return { radius: radius + (selected ? radius * 0.18 : 2),
    strokeWidth: radius * (selected ? 0.3 : 0.14) }
}


/** An armed source/destination gets a thin white ring inside its black border. */
export function territoryMarkerArmedRing(radius: number) {
  return { radius: radius * 0.87, strokeWidth: radius * 0.07 }
}
